export type BlockKind =
  | 'header'
  | 'customer_block'
  | 'scope_of_work'
  | 'scope_fields'
  | 'line_items_table'
  | 'total'
  | 'comments'
  | 'terms_conditions'
  | 'roles_responsibilities'
  | 'signature'
  | 'price_book'
  | 'custom_text'
  | 'custom'
  | 'spacer'
  | 'cover_page';

export const BLOCK_KINDS: readonly BlockKind[] = [
  'header',
  'customer_block',
  'scope_of_work',
  'scope_fields',
  'line_items_table',
  'total',
  'comments',
  'terms_conditions',
  'roles_responsibilities',
  'signature',
  'price_book',
  'custom_text',
  'custom',
  'spacer',
  'cover_page'
] as const;

/**
 * THE CREATABLE KINDS (LLAMS-617, design ruling 2026-09-01 — "the library is exactly the
 * set the product already has… the variety lives in the TEMPLATES, not the library").
 *
 * This is the offer for New Block (the only place that restricts itself to exactly this list) — not
 * the full `BlockKind` union. It also orders the Block Library's kind filter: `buildKindFilterOptions`
 * lists the kinds present in the results, these first, then every other supported kind
 * alphabetically by label, then "Other". Every other kind is still mapped, normalized and rendered
 * for blocks that already exist, and those blocks can be archived or duplicated, but the retired
 * ones (`RETIRED_BLOCK_KINDS` below) open READ-ONLY in the editor; only the OFFER is narrower:
 *   - `price_book` and `line_items_table` moved to the template level (2026-09-14 ruling) — a
 *     template's own Add panel offers them, not the block library.
 *   - `scope_fields`, `custom_text` and `cover_page` have no editor in this repo.
 *   - `comments` and `spacer` are legacy, read-compat-only kinds.
 *   - `custom` is creatable. The pdf-templates backend accepts `BlockKind.Custom`.
 */
export const CREATABLE_BLOCK_KINDS: readonly BlockKind[] = [
  'header',
  'customer_block',
  'scope_of_work',
  'roles_responsibilities',
  'total',
  'terms_conditions',
  'signature',
  'custom'
] as const;

/**
 * THE RETIRED KINDS (LLAMS-610 audit): no longer created and no longer edited in the block editor
 * (Figma `be.line-items-table` ... `be.spacer`: "no block of this kind can be opened"). A block of one
 * of these kinds still renders in templates and quotes and can be archived or duplicated, but it
 * opens READ-ONLY in the editor - no editing controls, no Save / Publish.
 */
export const RETIRED_BLOCK_KINDS: readonly BlockKind[] = [
  'line_items_table',
  'price_book',
  'scope_fields',
  'custom_text',
  'cover_page',
  'comments',
  'spacer'
] as const;

export function isRetiredBlockKind(kind: string | null | undefined): boolean {
  return kind != null && (RETIRED_BLOCK_KINDS as readonly string[]).includes(kind);
}

/**
 * Display labels (LLAMS-617 board pass, 2026-09-01/08). Display only — the wire `BlockKind`
 * value never changes (routes, DTOs, renderer registry, mappers all keep the original string).
 * The eight creatable kinds plus `price_book` and `line_items_table` (moved out of the library
 * but still filterable/viewable for existing blocks) carry the board's exact copy; every other
 * legacy kind keeps a readable label with no board ruling behind it.
 */
export const BLOCK_KIND_LABELS: Record<BlockKind, string> = {
  header: 'Letterhead',
  customer_block: 'Bill to / Job site',
  scope_of_work: 'Project scope',
  scope_fields: 'Scope fields',
  line_items_table: 'Line items',
  total: 'Totals',
  comments: 'Comments',
  terms_conditions: 'Terms',
  roles_responsibilities: 'Roles & responsibilities',
  signature: 'Signature',
  price_book: 'Price book',
  custom_text: 'Custom text',
  custom: 'Custom',
  spacer: 'Spacer',
  cover_page: 'Cover page'
};

/**
 * Legacy stored kinds the app edits and renders as another kind (LLAMS-610 BL-10). The stored
 * value is never rewritten: every PUT keeps sending the raw `blockType` it received.
 */
export const LEGACY_BLOCK_KIND_ALIASES: ReadonlyMap<string, BlockKind> = new Map<string, BlockKind>([['project_details', 'scope_fields']]);

/** Group name for blocks whose stored kind the app does not support (the Add panel's "Other" group). */
export const OTHER_BLOCK_KIND_LABEL = 'Other';

/** Kind-filter value that matches every stored kind the app does not support (shown as OTHER_BLOCK_KIND_LABEL). */
export const OTHER_KIND_FILTER_VALUE = 'other' as const;

/** What the Block Library's kind filter can hold: a supported kind, or the single other bucket. */
export type BlockKindFilterValue = BlockKind | typeof OTHER_KIND_FILTER_VALUE;
